import React, { useRef, useCallback } from 'react';
import './Pagination.css';

/** Mantiene la paginación en la misma posición visual tras cambiar de página. */
function mantenerPosicionPaginacion(root, anchorTop) {
  if (!root || anchorTop == null) return;
  const restore = () => {
    const delta = root.getBoundingClientRect().top - anchorTop;
    if (Math.abs(delta) > 2) {
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
    }
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(restore);
  });
  window.setTimeout(restore, 50);
  window.setTimeout(restore, 200);
}

/**
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {(page: number) => void} onPageChange — también se llama al clickear la página actual (para recargar)
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const rootRef = useRef(null);

  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > totalPages) return;
    const root = rootRef.current;
    const anchorTop = root ? root.getBoundingClientRect().top : null;
    onPageChange(page);
    mantenerPosicionPaginacion(root, anchorTop);
  }, [onPageChange, totalPages]);

  const evitarFocoScroll = (e) => {
    e.preventDefault();
  };

  if (totalPages <= 1) return null;

  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pageNumbers = [];
  for (let i = start; i <= end; i += 1) {
    pageNumbers.push(i);
  }

  const firstWindow = pageNumbers[0];
  const lastWindow = pageNumbers[pageNumbers.length - 1];

  return (
    <div className="pagination" ref={rootRef}>
      <button
        type="button"
        className="pagination-btn"
        onMouseDown={evitarFocoScroll}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Página anterior"
      >
        ← Anterior
      </button>
      <div className="pagination-numbers">
        {firstWindow > 1 && (
          <>
            <button type="button" className="pagination-btn" onMouseDown={evitarFocoScroll} onClick={() => handlePageChange(1)}>
              1
            </button>
            {firstWindow > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        {pageNumbers.map((page) => {
          const esActual = currentPage === page;
          return (
            <button
              key={page}
              type="button"
              className={`pagination-btn ${esActual ? 'pagination-btn-active' : ''}`}
              onMouseDown={evitarFocoScroll}
              onClick={() => handlePageChange(page)}
              aria-current={esActual ? 'page' : undefined}
              title={esActual ? `Recargar página ${page}` : `Ir a página ${page}`}
              aria-label={esActual ? `Recargar página ${page}` : `Página ${page}`}
            >
              {page}
            </button>
          );
        })}
        {lastWindow < totalPages && (
          <>
            {lastWindow < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button type="button" className="pagination-btn" onMouseDown={evitarFocoScroll} onClick={() => handlePageChange(totalPages)}>
              {totalPages}
            </button>
          </>
        )}
      </div>
      <button
        type="button"
        className="pagination-btn"
        onMouseDown={evitarFocoScroll}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Página siguiente"
      >
        Siguiente →
      </button>
    </div>
  );
};

export default Pagination;
